import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getCattegories } from '../../../../../../services/ProdutoService_ts';
import {
  createPricerAssignment,
  createPricingScopeLevel,
  deletePricerAssignment,
  getActiveIpaUsers,
  getAssignedPricingUsers,
  getPricerAssignment,
  getPricerAssignments,
  getPricingScopeCategories,
  getPricingScopeLevel,
  updatePricerAssignment,
  updatePricingScopeLevel,
} from '../service';
import { PricerAssignmentsParams, PricingScopeLevel } from '../types';

export const pricingScopeKeys = {
  root: ['ipa', 'setup', 'pricing-scope'] as const,
  level: () => [...pricingScopeKeys.root, 'level'] as const,
  assignmentsRoot: () => [...pricingScopeKeys.root, 'assignments'] as const,
  assignments: (params: PricerAssignmentsParams) => [...pricingScopeKeys.assignmentsRoot(), params] as const,
  assignment: (id?: string) => [...pricingScopeKeys.assignmentsRoot(), id] as const,
  assignedUsers: () => [...pricingScopeKeys.root, 'assigned-users'] as const,
  activeUsers: () => [...pricingScopeKeys.root, 'active-users'] as const,
  categories: (level?: number | null, search = '') => [...pricingScopeKeys.root, 'categories', level, search] as const,
  categoryLevels: () => [...pricingScopeKeys.root, 'category-levels'] as const,
};

export const usePricingScopeLevelQuery = () =>
  useQuery({
    queryKey: pricingScopeKeys.level(),
    queryFn: getPricingScopeLevel,
  });

export const usePricingScopeCategoryLevelsQuery = () =>
  useQuery({
    queryKey: pricingScopeKeys.categoryLevels(),
    queryFn: getCattegories,
  });

export const usePricerAssignmentsQuery = (params: PricerAssignmentsParams, enabled: boolean) =>
  useQuery({
    queryKey: pricingScopeKeys.assignments(params),
    queryFn: () => getPricerAssignments(params),
    enabled,
    keepPreviousData: true,
  });

export const usePricerAssignmentQuery = (id?: string, enabled = true) =>
  useQuery({
    queryKey: pricingScopeKeys.assignment(id),
    queryFn: () => getPricerAssignment(id!),
    enabled: enabled && !!id,
  });

export const useAssignedPricingUsersQuery = (enabled: boolean) =>
  useQuery({
    queryKey: pricingScopeKeys.assignedUsers(),
    queryFn: getAssignedPricingUsers,
    enabled,
  });

export const useActiveIpaUsersQuery = (enabled: boolean) =>
  useQuery({
    queryKey: pricingScopeKeys.activeUsers(),
    queryFn: getActiveIpaUsers,
    enabled,
  });

export const usePricingScopeCategoriesQuery = (level?: number | null, search = '', enabled = true) =>
  useQuery({
    queryKey: pricingScopeKeys.categories(level, search),
    queryFn: () => getPricingScopeCategories(level!, search),
    enabled: enabled && level != null,
    keepPreviousData: true,
  });

const useInvalidatePricingScopeAssignments = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: pricingScopeKeys.assignmentsRoot() }),
      queryClient.invalidateQueries({ queryKey: pricingScopeKeys.assignedUsers() }),
    ]);
};

export const useCreatePricingScopeLevelMutation = () => {
  const queryClient = useQueryClient();
  const invalidateAssignments = useInvalidatePricingScopeAssignments();

  return useMutation({
    mutationFn: (level: PricingScopeLevel) => createPricingScopeLevel(level.level),
    onSuccess: async (response, requestedLevel) => {
      await queryClient.cancelQueries({ queryKey: pricingScopeKeys.level() });
      queryClient.setQueryData(pricingScopeKeys.level(), response ? { ...requestedLevel, ...response } : requestedLevel);
      return invalidateAssignments();
    },
  });
};

export const useUpdatePricingScopeLevelMutation = () => {
  const queryClient = useQueryClient();
  const invalidateAssignments = useInvalidatePricingScopeAssignments();

  return useMutation({
    mutationFn: (level: PricingScopeLevel) => updatePricingScopeLevel(level.level),
    onSuccess: async (response, requestedLevel) => {
      await queryClient.cancelQueries({ queryKey: pricingScopeKeys.level() });
      queryClient.setQueryData(pricingScopeKeys.level(), response ? { ...requestedLevel, ...response } : requestedLevel);
      return invalidateAssignments();
    },
  });
};

export const useCreatePricerAssignmentMutation = () => {
  const invalidate = useInvalidatePricingScopeAssignments();
  return useMutation({ mutationFn: createPricerAssignment, onSuccess: invalidate });
};

export const useUpdatePricerAssignmentMutation = () => {
  const invalidate = useInvalidatePricingScopeAssignments();
  return useMutation({ mutationFn: updatePricerAssignment, onSuccess: invalidate });
};

export const useDeletePricerAssignmentMutation = () => {
  const invalidate = useInvalidatePricingScopeAssignments();
  return useMutation({ mutationFn: deletePricerAssignment, onSuccess: invalidate });
};
